import { ChakraBox, ChakraFlex, Text, FireflyCollapse } from '@equip.health/ui';

import OverviewLoading from '../../OverviewLoading';
import ProviderChannelTile from '~/components/maud/patients/profile/tabs/patientMessagesTab/ProviderChannelTile';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useState } from 'react';

import './MessagesMenu.css';
import { isExternalChannel } from '~/lib/util/chat/chat.utils';

interface MessagesMenuBodyProps {
  pinMessageChannels: GroupChannel[];
  newMessageChannels: GroupChannel[];
  newMessagesCount: number;
  isChannelsLoading: boolean;
  onChannelSelected: (
    channel: GroupChannel,
    isFromPinnedSection: boolean,
  ) => void;
  onSectionToggle: (sectionType: 'new' | 'pinned', isExpanded: boolean) => void;
  isMessagePinningEnabled: boolean;
}

const TOP_LEVEL_ID = 'messages-menu-body';

const getPinnedMessagesCount = (channel: GroupChannel) =>
  channel?.pinnedMessageIds?.length || 0;

const getAllPinnedMessagesCount = (channels: GroupChannel[]) =>
  channels?.reduce((acc, c) => acc + getPinnedMessagesCount(c), 0);

const MessagesMenuBody = ({
  pinMessageChannels,
  newMessageChannels,
  isChannelsLoading,
  onChannelSelected,
  onSectionToggle,
  newMessagesCount,
  isMessagePinningEnabled,
}: MessagesMenuBodyProps) => {
  const [isPinnedExpanded, setIsPinnedExpanded] = useState<boolean>(false);
  const [isNewExpanded, setIsNewExpanded] = useState<boolean>(true);
  const pinnedMessagesCount = getAllPinnedMessagesCount(pinMessageChannels);

  const handleNewExpanded = (value: boolean) => {
    setIsNewExpanded(value);
    onSectionToggle('new', value);
  };

  const handlePinnedExpanded = (value: boolean) => {
    setIsPinnedExpanded(value);
    onSectionToggle('pinned', value);
  };

  if (isChannelsLoading) {
    return (
      <ChakraFlex flexDirection="column" gap="20px" minHeight="500px" mt="10px">
        <OverviewLoading height={0} size="40px" />
      </ChakraFlex>
    );
  }

  return (
    <ChakraFlex
      flexDirection="column"
      gap="20px"
      minHeight="500px"
      mt="24px"
      px="12px"
    >
      {isMessagePinningEnabled && (
        <FireflyCollapse
          count={pinnedMessagesCount > 0 ? pinnedMessagesCount : undefined}
          id={`${TOP_LEVEL_ID}-pinned-messages-collapse`}
          isExpand={isPinnedExpanded}
          label="Pinned"
          onClick={handlePinnedExpanded}
        >
          {pinMessageChannels?.length > 0 ? (
            <ChakraBox mt={2}>
              {pinMessageChannels.map((channel, i) => (
                <ProviderChannelTile
                  channel={channel}
                  id={`${TOP_LEVEL_ID}-channels-pinned__${channel.url}`}
                  key={`${TOP_LEVEL_ID}-channels-pinned__${channel.url}-${i}`}
                  onClick={(channel) => onChannelSelected(channel, true)}
                  showPinned
                />
              ))}
            </ChakraBox>
          ) : (
            <Text my="10px">No pinned messages</Text>
          )}
        </FireflyCollapse>
      )}
      <FireflyCollapse
        count={newMessagesCount > 0 ? newMessagesCount : undefined}
        id={`${TOP_LEVEL_ID}-new-messages-collapse`}
        isExpand={isNewExpanded}
        label="New"
        onClick={handleNewExpanded}
      >
        {newMessageChannels?.length > 0 ? (
          <ChakraBox mt={2}>
            {newMessageChannels.map((channel, i) => (
              <ProviderChannelTile
                channel={channel}
                id={`${TOP_LEVEL_ID}-channels-new__${channel.url}`}
                key={`${TOP_LEVEL_ID}-channels-new__${channel.url}-${i}`}
                onClick={(channel) => onChannelSelected(channel, false)}
                showMarkAsRead={isExternalChannel(channel.url)}
              />
            ))}
          </ChakraBox>
        ) : (
          <Text my="10px">No new messages</Text>
        )}
      </FireflyCollapse>
    </ChakraFlex>
  );
};

export default MessagesMenuBody;
